import React from "react";

/**
 * Normalizes a stored social/website URL into a safe external href.
 * Stored values can predate form validation or be set via the API, so we
 * defend at render time: prepend https:// when no scheme is present, and
 * reject anything that isn't http(s) (e.g. javascript:) to prevent XSS.
 *
 * Returns null when the value is blank or unsafe, so the link is dropped.
 */
const toSafeHref = (rawUrl) => {
  if (!rawUrl) return null;
  const trimmed = String(rawUrl).trim();
  if (!trimmed) return null;

  const withScheme = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;

  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
      return null;
    }
    return parsed.href;
  } catch {
    return null;
  }
};

const SocialMediaIcons = ({ socialMediaLinks, className = "" }) => {
  if (!socialMediaLinks) return null;

  const socialIcons = [
    {
      key: "website",
      url: socialMediaLinks.website,
      label: "Website",
      icon: (
        <svg
          className="w-5 h-5 transition-colors"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zM9.797 4.4A8.03 8.03 0 0 0 5.07 8h2.642c.27-1.318.69-2.523 1.23-3.518a8.2 8.2 0 0 0-.145-.082zM8 12c0-1.052.072-2.05.202-2.97h-2.91A8.03 8.03 0 0 0 4 12c0 1.04.198 2.034.557 2.95h2.726A21.6 21.6 0 0 1 8 12zm-2.93 4.95h2.642c.27 1.318.69 2.523 1.23 3.518l-.144.082A8.03 8.03 0 0 1 5.07 16.95zm4.7 0a13.2 13.2 0 0 0 .98 2.756c.426.844.873 1.422 1.25 1.728V16.95H9.84zm-.55-2H12V9.05H9.29A19.6 19.6 0 0 0 9.08 12c0 1.04.075 2.034.21 2.95zm4.71 2v4.484c.377-.306.824-.884 1.25-1.728.392-.776.726-1.71.98-2.756H13.93zm.55-2h2.71c.135-.916.21-1.91.21-2.95s-.075-2.034-.21-2.95h-2.71V14.95zm-.55-7.9c-.254-1.046-.588-1.98-.98-2.756-.426-.844-.873-1.422-1.25-1.728V7.05h2.23zm-4.16 0H12V2.566c-.377.306-.824.884-1.25 1.728-.392.776-.726 1.71-.98 2.756zm6.49 0h2.642a8.03 8.03 0 0 0-4.727-3.6l-.144.082c.54.995.96 2.2 1.23 3.518zm.477 2c.13.92.202 1.918.202 2.97 0 1.04-.082 2.02-.226 2.95h2.726c.36-.916.557-1.91.557-2.95 0-1.052-.198-2.05-.557-2.97h-2.91zm-.477 7.9c-.27 1.318-.69 2.523-1.23 3.518l.144.082A8.03 8.03 0 0 0 18.93 16.95h-2.642z"
          />
        </svg>
      ),
    },
    {
      key: "instagram",
      url: socialMediaLinks.instagram,
      label: "Instagram",
      icon: (
        <svg
          className="w-5 h-5 transition-colors"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12.017 0C8.396 0 7.989.013 7.041.048 6.094.084 5.52.199 5.012.374a6.5 6.5 0 0 0-2.346 1.528 6.5 6.5 0 0 0-1.528 2.346c-.175.508-.29 1.082-.326 2.029C.013 7.325 0 7.732 0 11.353c0 3.621.013 4.028.048 4.975.036.947.151 1.521.326 2.029a6.5 6.5 0 0 0 1.528 2.346 6.5 6.5 0 0 0 2.346 1.528c.508.175 1.082.29 2.029.326.947.035 1.354.048 4.975.048s4.028-.013 4.975-.048c.947-.036 1.521-.151 2.029-.326a6.5 6.5 0 0 0 2.346-1.528 6.5 6.5 0 0 0 1.528-2.346c.175-.508.29-1.082.326-2.029.035-.947.048-1.354.048-4.975s-.013-4.028-.048-4.975c-.036-.947-.151-1.521-.326-2.029a6.5 6.5 0 0 0-1.528-2.346A6.5 6.5 0 0 0 19.049.374c-.508-.175-1.082-.29-2.029-.326C16.073.013 15.666 0 12.017 0zM12.017 2.162c3.557 0 3.98.013 5.38.048.854.036 1.317.166 1.626.276.409.159.7.348 1.007.655.307.307.496.598.655 1.007.11.309.24.772.276 1.626.035 1.4.048 1.823.048 5.38s-.013 3.98-.048 5.38c-.036.854-.166 1.317-.276 1.626-.159.409-.348.7-.655 1.007-.307.307-.598.496-1.007.655-.309.11-.772.24-1.626.276-1.4.035-1.823.048-5.38.048s-3.98-.013-5.38-.048c-.854-.036-1.317-.166-1.626-.276a2.717 2.717 0 0 1-1.007-.655 2.717 2.717 0 0 1-.655-1.007c-.11-.309-.24-.772-.276-1.626-.035-1.4-.048-1.823-.048-5.38s.013-3.98.048-5.38c.036-.854.166-1.317.276-1.626.159-.409.348-.7.655-1.007.307-.307.598-.496 1.007-.655.309-.11.772-.24 1.626-.276 1.4-.035 1.823-.048 5.38-.048z" />
          <path d="M12.017 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12.017 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z" />
        </svg>
      ),
    },
    {
      key: "facebook",
      url: socialMediaLinks.facebook,
      label: "Facebook",
      icon: (
        <svg
          className="w-5 h-5 transition-colors"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    },
    {
      key: "twitter",
      url: socialMediaLinks.twitter,
      label: "X",
      icon: (
        <svg
          className="w-5 h-5 transition-colors"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
    },
    {
      key: "linkedin",
      url: socialMediaLinks.linkedin,
      label: "LinkedIn",
      icon: (
        <svg
          className="w-5 h-5 transition-colors"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
    },
  ];

  const validLinks = socialIcons
    .map((social) => ({ ...social, href: toSafeHref(social.url) }))
    .filter((social) => social.href);

  if (validLinks.length === 0) return null;

  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {validLinks.map((social) => (
        <a
          key={social.key}
          href={social.href}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center space-x-2 text-sm text-themed-secondary hover:text-themed-primary transition-colors"
          title={social.label}
        >
          {social.icon}
          <span className="sr-only">{social.label}</span>
        </a>
      ))}
    </div>
  );
};

export default SocialMediaIcons;
